"use client";

import { useState, useRef } from "react";
import { Box, Avatar, CircularProgress, Link } from "@mui/material";
import {
  Dialog,
  DialogPortal,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogClose,
  Button,
  ConfirmButton,
} from "@/ui";
import TextField from "@components/shared/form/TextField";
import { AITextField } from "@components/shared/form/ai";
import { createRecruitingPrompt } from "@/lib/agent/utils/candidateToPrompt";
import FormSection from "./parts/FormSection";
import FormFieldTitle from "./parts/FormFieldTitle";

export default function EditRoles({ onClose, open, role, onUpdate, onDelete, candidate }) {
  const [roleData, setRoleData] = useState({
    name: role?.name || "",
    role: role?.role || "",
    time_period: role?.time_period || "",
    description: role?.description || "",
    image: role?.image || "",
    image_data: null,
  });
  const [imageLoading, setImageLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (event) => {
    setRoleData({ ...roleData, [event.target.name]: event.target.value });
  };

  const handleInputAIChange = (field) => (value) => {
    setRoleData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    onUpdate({ ...role, ...roleData });
    onClose();
  };

  const handleUploadImageClick = (event) => {
    event.preventDefault();
    fileInputRef.current?.click();
  };

  const handleImageRemove = (event) => {
    event.preventDefault();
    setRoleData((prev) => ({ ...prev, image: "", image_data: null }));
  };

  const handleUpdateImage = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setImageLoading(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setRoleData((prev) => ({ ...prev, image: reader.result, image_data: file }));
      setImageLoading(false);
    };
    reader.onerror = () => setImageLoading(false);
    reader.readAsDataURL(file);
  };

  const handleDeleteRole = () => {
    onDelete(role.id);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPortal>
        <DialogBackdrop />
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Edit role details</DialogTitle>
            <DialogClose />
          </DialogHeader>
          <DialogBody>
            <FormSection>
              <FormFieldTitle>Company name:</FormFieldTitle>
              <TextField fullWidth name="name" variant="outlined" value={roleData.name} onChange={handleChange} />
            </FormSection>
            <FormSection>
              <FormFieldTitle>Role title:</FormFieldTitle>
              <TextField fullWidth name="role" variant="outlined" value={roleData.role} onChange={handleChange} />
            </FormSection>
            <FormSection>
              <FormFieldTitle>Time period:</FormFieldTitle>
              <TextField fullWidth name="time_period" variant="outlined" value={roleData.time_period} onChange={handleChange} />
            </FormSection>
            <FormSection>
              <FormFieldTitle>Description:</FormFieldTitle>
              <AITextField
                value={roleData.description || ""}
                onChange={handleInputAIChange("description")}
                variant="textarea"
                placeholder="Describe your role and key achievements..."
                prompt={
                  createRecruitingPrompt(candidate, "general") +
                  `. This is a role description for the position "${roleData.role || "role"}" at "${roleData.name || "company"}" during "${roleData.time_period || "time period"}". Write a compelling description that highlights key responsibilities, achievements, skills utilized, and impact created. Focus on quantifiable results and technical/professional growth. Make it suitable for recruiting presentations.`
                }
                minRows={4}
                maxRows={8}
                fullWidth
              />
            </FormSection>
            <FormSection sx={{ flexDirection: "row", display: "flex", gap: "16px" }}>
              <FormFieldTitle>Company logo:</FormFieldTitle>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", alignItems: "center" }}>
                {!imageLoading && (
                  <Avatar src={roleData.image} alt={roleData.name} sx={{ width: 84, height: 84, fontSize: "32px", fontWeight: 600, backgroundColor: "#e0e7ff", color: "var(--primitive-indigo-600, #13319a)" }}>
                    {roleData.name.substring(0, 2)}
                  </Avatar>
                )}
                {imageLoading && <CircularProgress size={80} color="secondary" />}
                {!imageLoading && !roleData.image && (
                  <Link href="#" onClick={handleUploadImageClick}>Upload image</Link>
                )}
                {!imageLoading && roleData.image && (
                  <Link href="#" onClick={handleImageRemove}>Remove</Link>
                )}
                <input hidden accept="image/*" type="file" onChange={handleUpdateImage} ref={fileInputRef} />
              </Box>
            </FormSection>
          </DialogBody>
          <DialogFooter style={{ justifyContent: "space-between" }}>
            <ConfirmButton
              text="Delete role"
              onUpdate={handleDeleteRole}
              variant="tertiary"
              color="red"
            />
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
              <Button variant="primary" onClick={handleSubmit}>Save changes</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
